import React from 'react';
import { Document, Page, Text, Font, View, StyleSheet } from '@react-pdf/renderer';
import CoverPage from './sections/CoverPage';
import DisclaimerPage from './sections/DisclaimerPage';
import ExecutiveTableOfContentsPage from './sections/ExecutiveTableOfContentsPage';
import DocumentReferencePage from './sections/DocumentReferencePage';
import ExecutiveSummaryPage from './sections/ExecutiveSummaryPage';
import ScopePage from './sections/ScopePage';
import ProjectObjectivesPage from './sections/ProjectObjectivesPage';
import VulnerabilityRatingDefinitionsPage from './sections/VulnerabilityRatingDefinitionsPage';
import AppendixOwaspRiskRatingPages from './sections/AppendixOwaspRiskRatingPages';
import MethodologyPage from './sections/MethodologyPage';
import NetworkMethodologyPage from './sections/NetworkMethodologyPage';
import MobileMethodologyPage from './sections/MobileMethodologyPage';
import SourceCodeMethodologyPage from './sections/SourceCodeMethodologyPage';
import AboutUsPage from './sections/AboutUsPage';
import { ReportData } from '../types/report.types';
import { SectionPageProvider, useSectionPages } from './SectionPageContext';
import { filterReportDataByRunId } from '../utils/findingsUtils';
import { processDisclaimerContent, registerReportFonts } from '../utils/reportSharedUtils';

// Register fonts once globally
registerReportFonts();

interface ExecutiveSummaryTemplatePDFProps {
  reportData: ReportData;
  processing?: boolean;
}

const styles = StyleSheet.create({
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 10,
    color: 'grey',
  },
});

const ExecutiveSummaryTemplatePDFContent: React.FC<ExecutiveSummaryTemplatePDFProps> = ({
  reportData: rawReportData,
  processing,
}) => {
  const reportData = React.useMemo(() => filterReportDataByRunId(rawReportData) || rawReportData, [rawReportData]);
  const disclaimerParagraphs = processDisclaimerContent((reportData.disclaimer ?? '') || '');
  const { updateSectionPage } = useSectionPages();
  
  if (processing) {
    return (
      <Document>
        <Page>
          <Text>Loading...</Text>
        </Page>
      </Document>
    );
  }

  // Helper function to render pages with footer
  const renderPageWithFooter = (pageComponent: React.ReactElement, key: string) => {
    return React.cloneElement(pageComponent, {
      key,
      children: [
        ...(React.Children.toArray(pageComponent.props.children) || []),
        <View key="footer" style={styles.footer} fixed>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      ]
    });
  };

  return (
    <Document>
      <CoverPage reportData={reportData} />
      <DisclaimerPage
        reportData={reportData}
        disclaimerParagraphs={disclaimerParagraphs}
        processDisclaimerContent={processDisclaimerContent}
        sectionId="Disclaimer"
      />
      <ExecutiveTableOfContentsPage reportData={reportData} />
      {renderPageWithFooter(<DocumentReferencePage reportData={reportData} sectionId="DocumentReference" />, 'doc-ref')}
      {renderPageWithFooter(<ExecutiveSummaryPage
        reportData={reportData}
        sectionId="ExecutiveSummary"
      />, 'exec-summary')}
      {renderPageWithFooter(<ScopePage reportData={reportData} sectionId="Scope" />, 'scope')}
      {renderPageWithFooter(<ProjectObjectivesPage reportData={reportData} sectionId="ProjectObjectives" />, 'objectives')}
      {renderPageWithFooter(<VulnerabilityRatingDefinitionsPage reportData={reportData} sectionId="VulnerabilityRatingDefinitions" />, 'vuln-defs')}
      
      {/* Appendix and Methodologies */}
      {AppendixOwaspRiskRatingPages({ 
        documentNumber: reportData.document_number || 'Document Number', 
        registerSectionPage: updateSectionPage, 
        sectionId: 'AppendixOwaspRiskRating_Page1' 
      })
        .filter(React.isValidElement)
        .map((page, idx) => React.cloneElement(page, { key: `appendix-${idx}` }))}
      
      {/* Methodology sections based on what's enabled */}
      {(reportData.methodology?.web) && (
        <MethodologyPage
          documentNumber={reportData.document_number}
          registerSectionPage={updateSectionPage}
          sectionId="Methodology"
        />
      )}
      {(reportData.methodology?.network) && (
        <NetworkMethodologyPage
          documentNumber={reportData.document_number}
          registerSectionPage={updateSectionPage}
          sectionId="NetworkMethodology"
        />
      )}
      {(reportData.methodology?.mobile) && (
        <MobileMethodologyPage
          documentNumber={reportData.document_number}
          registerSectionPage={updateSectionPage}
          sectionId="MobileMethodology"
        />
      )}
      {(reportData.methodology?.sourceCode) && (
        <SourceCodeMethodologyPage
          documentNumber={reportData.document_number}
          registerSectionPage={updateSectionPage}
          sectionId="SourceCodeMethodology"
        />
      )}
      <AboutUsPage />
    </Document>
  );
};

const ExecutiveSummaryTemplatePDF: React.FC<ExecutiveSummaryTemplatePDFProps> = (props) => {
  return (
    <SectionPageProvider>
      <ExecutiveSummaryTemplatePDFContent {...props} />
    </SectionPageProvider>
  );
};

export default ExecutiveSummaryTemplatePDF;
